import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import {
  BookOpen, Heart, Eye, ArrowRight, Loader2, Users,
  Sparkles, TrendingUp, UserPlus, Trophy, ChevronDown,
  Zap, MessageCircle, MessageSquare, Flame, Share2
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { shareStudyKit } from '../utils/whatsapp';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: 'all', label: 'For You', icon: Sparkles },
  { id: 'trending', label: 'Trending', icon: TrendingUp },
  { id: 'following', label: 'Following', icon: Users },
];

const FeedPage = () => {
  const [feed, setFeed] = useState([]);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [discover, setDiscover] = useState([]);

  const fetchFeed = useCallback(async (p = 1, append = false) => {
    if (p === 1) setLoading(true); else setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/social/feed?page=${p}&limit=15&filter=${filter}`);
      setFeed(prev => append ? [...prev, ...res.data.feed] : res.data.feed || []);
      setHasMore(res.data.has_more);
      setPage(p);
    } catch (err) { console.error(err); }
    finally { setLoading(false); setLoadingMore(false); }
  }, [filter]);

  const fetchDiscover = useCallback(async () => {
    try { const res = await axios.get(`${API}/social/discover?limit=5`); setDiscover(res.data.students || []); }
    catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchFeed(1); }, [fetchFeed]);
  useEffect(() => { fetchDiscover(); }, [fetchDiscover]);

  const handleUpvote = async (shareId, idx) => {
    try {
      const res = await axios.post(`${API}/social/upvote/${shareId}`);
      setFeed(prev => prev.map((item, i) => i === idx ? { ...item, is_upvoted: res.data.upvoted, upvotes: item.upvotes + (res.data.upvoted ? 1 : -1) } : item));
    } catch (err) { toast.error('Failed'); }
  };

  const handleFollow = async (targetId) => {
    try {
      const res = await axios.post(`${API}/social/follow/${targetId}`);
      toast.success(`Following ${res.data.following}!`);
      setDiscover(prev => prev.filter(s => s.id !== targetId));
      fetchFeed(1);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-96" data-testid="feed-loading">
      <Loader2 className="w-10 h-10 animate-spin text-[#1ED760]" />
    </div>
  );

  return (
    <div className="f500-page max-w-2xl mx-auto space-y-6 pb-20 lg:pb-6" data-testid="feed-page">
      {/* Header */}
      <div className="flex items-center justify-between isp-animate-in">
        <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">Feed</h1>
        <Link to="/lectures">
          <button className="isp-btn-primary text-sm flex items-center gap-2" data-testid="feed-share-btn">
            <Share2 className="w-4 h-4" /> Share Notes
          </button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-2 overflow-x-auto pb-1 isp-animate-in" style={{ animationDelay: '0.1s' }} data-testid="feed-filters">
        {FILTERS.map(f => (
          <button key={f.id} onClick={() => { setFilter(f.id); setPage(1); }}
            className={`isp-tag px-4 py-2 whitespace-nowrap cursor-pointer transition-all ${filter === f.id ? 'bg-[#1ED760] text-black border-[#1ED760] font-bold' : 'hover:bg-white/10'}`}
            data-testid={`feed-filter-${f.id}`}>
            <f.icon className="w-3.5 h-3.5" /> {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Feed */}
        <div className="lg:col-span-2 space-y-4 isp-stagger">
          {feed.length === 0 ? (
            <div className="isp-card text-center py-12" data-testid="feed-empty">
              <Flame className="w-14 h-14 mx-auto mb-4 text-[#FF715B] isp-flame" />
              <p className="text-xl font-bold text-white">
                {filter === 'following' ? 'Follow students to see their notes!' : 'No study kits yet'}
              </p>
              <p className="text-sm text-zinc-500 mt-1">Be the first to share and earn XP</p>
              <Link to="/lectures"><button className="isp-btn-primary mt-5 text-sm"><Share2 className="w-4 h-4 inline mr-2" /> Share a Study Kit</button></Link>
            </div>
          ) : (
            <>
              {feed.map((item, i) => (
                <motion.div key={item.share_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04, duration: 0.3 }}>
                  <FeedCard item={item}
                    onUpvote={() => handleUpvote(item.share_id, i)}
                    onShare={() => shareStudyKit(item.share_id, item.title, item.flashcards_count, item.quizzes_count)}
                    onFollow={() => handleFollow(item.author.id)} />
                </motion.div>
              ))}
              {hasMore && (
                <div className="text-center py-4">
                  <button onClick={() => fetchFeed(page + 1, true)} disabled={loadingMore} className="isp-btn-secondary text-sm" data-testid="feed-load-more">
                    {loadingMore ? <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> : <ChevronDown className="w-4 h-4 inline mr-2" />} Load More
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-5 hidden lg:block">
          {discover.length > 0 && (
            <div className="isp-card" data-testid="discover-panel">
              <h3 className="text-xs font-bold text-zinc-400 mb-4 uppercase tracking-[0.2em]">
                <UserPlus className="w-4 h-4 text-[#FF715B] inline mr-2" /> Who to Follow
              </h3>
              <div className="space-y-3">
                {discover.map(s => (
                  <div key={s.id} className="flex items-center gap-3" data-testid={`discover-student-${s.id}`}>
                    <div className="w-10 h-10 rounded-full isp-avatar bg-gradient-to-br from-[#1ED760] to-[#1DB954] flex items-center justify-center text-black text-sm font-bold flex-shrink-0">
                      {(s.name || 'S')[0].toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-white truncate">{s.name}</p>
                      <p className="text-[10px] text-zinc-500">Lv.{s.level} · {s.shared_kits} kits</p>
                    </div>
                    <button onClick={() => handleFollow(s.id)} className="isp-btn-primary text-xs px-3 py-1.5" data-testid={`follow-btn-${s.id}`}>Follow</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="isp-card bg-gradient-to-br from-[#FF715B]/10 to-[#F5A623]/10 border-[#FF715B]/20" data-testid="feed-cta">
            <Trophy className="w-8 h-8 text-[#F5A623] mb-2 isp-flame" />
            <h3 className="text-base font-bold text-white">Challenge a Friend</h3>
            <p className="text-xs text-zinc-400 mt-1">Take a quiz and dare your classmates to beat your score!</p>
            <Link to="/exam-mode"><button className="isp-btn-coral w-full mt-3 text-xs">Start Quiz <ArrowRight className="w-3 h-3 inline ml-1" /></button></Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const FeedCard = ({ item, onUpvote, onShare, onFollow }) => (
  <div className="isp-card" data-testid={`feed-item-${item.share_id}`}>
    {/* Author */}
    <div className="flex items-center gap-3 mb-3">
      <div className="w-10 h-10 rounded-full isp-avatar bg-gradient-to-br from-[#3B82F6] to-[#1ED760] flex items-center justify-center text-white text-sm font-bold flex-shrink-0">
        {(item.author.name || 'S')[0].toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-white">{item.author.name}</p>
        <p className="text-[10px] text-zinc-500">{item.author.xp} XP · {new Date(item.created_at).toLocaleDateString()}</p>
      </div>
      {!item.is_own && !item.is_following_author && (
        <button onClick={onFollow} className="isp-tag bg-[#1ED760]/10 text-[#1ED760] border-[#1ED760]/30 cursor-pointer hover:bg-[#1ED760]/20" data-testid={`feed-follow-${item.share_id}`}>
          <UserPlus className="w-3 h-3" /> Follow
        </button>
      )}
    </div>

    {/* Content */}
    <Link to={`/share/${item.share_id}`} className="block group">
      <h3 className="text-lg font-bold text-white group-hover:text-[#1ED760] transition-colors leading-snug">{item.title}</h3>
      {item.subject_name && <span className="isp-tag mt-2 bg-[#F5A623]/10 text-[#F5A623] border-[#F5A623]/30">{item.subject_name}</span>}
    </Link>

    {/* Badges */}
    <div className="flex gap-2 mt-3 flex-wrap">
      {item.flashcards_count > 0 && <span className="isp-tag bg-blue-500/10 text-blue-400 border-blue-500/20"><BookOpen className="w-3 h-3" /> {item.flashcards_count} flashcards</span>}
      {item.quizzes_count > 0 && <span className="isp-tag bg-green-500/10 text-[#1ED760] border-green-500/20"><MessageCircle className="w-3 h-3" /> {item.quizzes_count} quizzes</span>}
      {item.has_summary && <span className="isp-tag bg-amber-500/10 text-[#F5A623] border-amber-500/20"><Sparkles className="w-3 h-3" /> Summary</span>}
    </div>

    {/* Actions */}
    <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/5">
      <div className="flex items-center gap-5">
        <button aria-label="Like" onClick={onUpvote} className={`flex items-center gap-1.5 text-sm font-semibold transition-all ${item.is_upvoted ? 'text-[#FF715B]' : 'text-zinc-500 hover:text-[#FF715B]'}`} data-testid={`upvote-${item.share_id}`}>
          <Heart className={`w-5 h-5 transition-transform ${item.is_upvoted ? 'fill-[#FF715B] scale-110' : 'hover:scale-110'}`} /> {item.upvotes || 0}
        </button>
        <span className="flex items-center gap-1.5 text-sm text-zinc-500"><Eye className="w-5 h-5" /> {item.views || 0}</span>
        <button onClick={onShare} className="text-zinc-500 hover:text-green-500 transition-colors" data-testid={`share-${item.share_id}`}><MessageSquare className="w-5 h-5" /></button>
      </div>
      <Link to={`/share/${item.share_id}`}>
        <button className="isp-btn-primary text-xs px-4 py-2" data-testid={`view-kit-${item.share_id}`}>
          Study <ArrowRight className="w-3 h-3 inline ml-1" />
        </button>
      </Link>
    </div>
  </div>
);

export default FeedPage;
